/**
 * Display-layer per-axis clamp: max(config, floor) against the served v1.2
 * shape_floor payload (A1).
 *
 * Hard rules:
 * - Display-layer ONLY. No planner math, no floor re-derivation: every floor
 *   value is read straight from the payload; the sections axis uses the
 *   adapter-served `body_section_floor` (already in config body-section
 *   units) so this module does plain max() with zero unit knowledge.
 * - advisory_* payload fields are NEVER consumed here (hard floors only).
 * - When the payload is absent/unparseable, config values pass through
 *   unclamped and every `floored` flag is false (under-promise degradation).
 * - defaultConfigToRecipe remains the ONLY config deserializer and gains
 *   ZERO math — this module consumes its output, never modifies it.
 */

import { DEFAULT_RECIPE, GenerationRecipe } from '../recipeTypes';
import {
  GlyphSlot,
  ShapeFloorPayload,
  glyphStatesFromFloor,
} from './shapeFloor';

/** One clamped axis: the display value plus whether the floor raised it. */
export interface ClampedAxis<T> {
  value: T;
  /** True when the floor forced the display value above the config value —
   *  the sole input to the superscript floor tick (CONSULTS §4). */
  floored: boolean;
}

/** The clamped, floor-aware display shape of a recipe. */
export interface ClampedRecipeShape {
  words: ClampedAxis<number>;
  minSections: ClampedAxis<number>;
  maxSections: ClampedAxis<number>;
  faqItems: ClampedAxis<number>;
  callouts: ClampedAxis<number>;
  images: ClampedAxis<number>;
  /** Display presence of H3 subsections: config OR hard floor (min_h3 >= 1).
   *  Pure display logic per the S1 ruling — advisory_min_h3 is ignored. */
  h3Present: boolean;
  /** Display presence of the CTA: config OR hard floor
   *  (require_cta_before_faq). S2-C2: a Shape may never omit a component the
   *  gate hard-requires. advisory_require_cta_before_faq NEVER forces. */
  ctaPresent: boolean;
  /** Effective per-slot baseline presence: recipe config OR floor require_*. */
  glyphs: Record<GlyphSlot, boolean>;
}

function clampAxis(configValue: number, floorValue: number | undefined): ClampedAxis<number> {
  if (floorValue === undefined || configValue >= floorValue) {
    return { value: configValue, floored: false };
  }
  return { value: floorValue, floored: true };
}

/**
 * Clamp a recipe's display values against its shape floor.
 *
 * @param recipe  Output of defaultConfigToRecipe (the ONLY deserializer).
 * @param floor   Parsed v1.2 payload (parseShapeFloor output), or undefined
 *                when absent/malformed — config passes through unclamped.
 */
export function clampRecipeToFloor(
  recipe: GenerationRecipe,
  floor: ShapeFloorPayload | undefined,
): ClampedRecipeShape {
  const rules = floor?.rules;

  const words = clampAxis(recipe.targetWordCount, rules?.min_words);

  const configMin = recipe.minSections ?? DEFAULT_RECIPE.minSections ?? 3;
  const minSections = clampAxis(configMin, floor?.body_section_floor);
  const configMax = Math.max(
    recipe.maxSections ?? DEFAULT_RECIPE.maxSections ?? 8,
    configMin,
  );
  // The floor raises the MIN only; the max follows the min upward so the
  // displayed range never inverts. max itself is never "floored".
  const maxSections: ClampedAxis<number> = {
    value: Math.max(configMax, minSections.value),
    floored: false,
  };

  const faqItems = clampAxis(
    recipe.maxFAQItems ?? DEFAULT_RECIPE.maxFAQItems ?? 5,
    rules?.min_faq_items,
  );

  const configCallouts =
    recipe.maxCalloutsPerArticle ?? DEFAULT_RECIPE.maxCalloutsPerArticle ?? 0;
  const callouts = clampAxis(configCallouts, rules?.min_callouts);

  const configImages = recipe.minImages ?? DEFAULT_RECIPE.minImages ?? 0;
  const images = clampAxis(configImages, rules?.min_image_scaffolds);

  // H3 display presence: config toggle OR hard floor. Advisory ignored.
  const h3Present =
    (recipe.includesH3Subsections ?? DEFAULT_RECIPE.includesH3Subsections ?? false) ||
    (rules !== undefined && rules.min_h3 >= 1);

  // CTA display presence (S2-C2): config toggle OR the HARD floor flag only —
  // advisory_require_cta_before_faq must never force the Shape.
  const ctaPresent =
    (recipe.includesCTA ?? DEFAULT_RECIPE.includesCTA ?? false) ||
    (rules !== undefined && rules.require_cta_before_faq === true);

  // Effective baseline presence per slot: recipe config OR floor require_*.
  const floorGlyphs = rules ? glyphStatesFromFloor(rules) : undefined;
  const glyphs: Record<GlyphSlot, boolean> = {
    toc:
      (recipe.includesTableOfContents ?? DEFAULT_RECIPE.includesTableOfContents ?? false) ||
      (floorGlyphs?.toc ?? false),
    keyTakeaways: floorGlyphs?.keyTakeaways ?? true, // R7-class baseline
    list:
      (recipe.includesList ?? DEFAULT_RECIPE.includesList) ||
      (floorGlyphs?.list ?? false),
    table:
      (recipe.includesTables ?? DEFAULT_RECIPE.includesTables ?? false) ||
      (floorGlyphs?.table ?? false),
    callout:
      (recipe.includesCallouts ?? DEFAULT_RECIPE.includesCallouts ?? false) ||
      (floorGlyphs?.callout ?? false),
    image:
      (recipe.includesImages ?? DEFAULT_RECIPE.includesImages) ||
      (floorGlyphs?.image ?? false),
    faq: true, // R7 — always-on, never a toggle
  };

  return { words, minSections, maxSections, faqItems, callouts, images, h3Present, ctaPresent, glyphs };
}
